// src/App.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import "./App.scss";
import {
    ChatPanel,
    type SessionControl,
} from "./components/ChatPanel/ChatPanel";
import DigitalHumanChat, {
    DigitalHumanChatRef,
} from "./components/DigitalHuman/DigitalHumanChat";
import { StatusBar } from "./components/StatusBar/StatusBar";
import { useAudioPlayer } from "./hooks/useAudioPlayer";
import { useStreamingSocket } from "./hooks/useStreamingSocket";
import type { ChatMessage } from "./types";
import {
    AVATAR_HTTP_CONFIG,
    TTS_TAIL_CONFIG,
    WS_CONFIG,
} from "./utils/constants";
import {
    formatAvatarSessionError,
    isAvatarBusyError,
} from "./utils/avatarSessionError";
import {
    concatArrayBuffers,
    decodeBase64ToArrayBuffer,
    toEvenArrayBuffer,
} from "./utils/pcm";

type InquiryPhase = "landing" | "inquiry";

const App: React.FC = () => {
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [isSessionActive, setIsSessionActive] = useState(false);
    const [isSessionStarting, setIsSessionStarting] = useState(false);
    const [phase, setPhase] = useState<InquiryPhase>("landing");
    const [avatarReady, setAvatarReady] = useState(false);
    const [avatarSpeaking, setAvatarSpeaking] = useState(false);
    const [avatarError, setAvatarError] = useState<string | null>(null);
    const [browserAudioFallback, setBrowserAudioFallback] = useState(false);
    const [sessionControl, setSessionControl] = useState<SessionControl>(null);

    const sessionNonceRef = useRef(0);
    const sessionStartedRef = useRef(false);
    const leavingRef = useRef(false);
    const avatarRetryRef = useRef(0);
    const avatarReadyRef = useRef(false);
    const ttsFinishTimerRef = useRef<number | null>(null);
    const ttsTurnBytesRef = useRef(0);
    const ttsTurnStartRef = useRef(0);
    const lastChunkAtRef = useRef(0);
    const lastChunkBytesRef = useRef(0);
    const ttsSampleRateRef = useRef(TTS_TAIL_CONFIG.SAMPLE_RATE);
    const pendingTtsRef = useRef<ArrayBuffer[]>([]);
    const pendingTtsFinishRef = useRef(false);
    const digitalHumanRef = useRef<DigitalHumanChatRef>(null);
    const browserAudioFallbackRef = useRef(false);
    const browserChunksRef = useRef<ArrayBuffer[]>([]);

    const { play: playBrowserTts, stop: stopBrowserTts } = useAudioPlayer({
        onEnded: () => setAvatarSpeaking(false),
        onError: () => setAvatarSpeaking(false),
    });

    const {
        isConnected,
        sendMessage,
        subscribe,
        reconnect,
        reconnectAttempts,
        reconnectExhausted,
        maxReconnectAttempts,
    } = useStreamingSocket(WS_CONFIG.URL);

    const clearTtsFinishTimer = () => {
        if (ttsFinishTimerRef.current) {
            clearTimeout(ttsFinishTimerRef.current);
            ttsFinishTimerRef.current = null;
        }
    };

    /** 播报收口兜底：仅作安全网；正常靠 onPlaybackEnd。延迟偏长，避免过早开麦吃到数字人尾音 */
    const scheduleAvatarSpeakingRelease = useCallback(() => {
        clearTtsFinishTimer();
        const sampleRate = Math.max(1, ttsSampleRateRef.current);
        const pcmMs = (ttsTurnBytesRef.current / 2 / sampleRate) * 1000;
        const elapsed =
            ttsTurnStartRef.current > 0
                ? Date.now() - ttsTurnStartRef.current
                : 0;
        const remainMs = Math.max(0, pcmMs - elapsed);
        const delay =
            remainMs +
            TTS_TAIL_CONFIG.SILENCE_MS +
            TTS_TAIL_CONFIG.FINISH_EXTRA_MS +
            TTS_TAIL_CONFIG.RESUME_GUARD_MS +
            1500;
        ttsFinishTimerRef.current = window.setTimeout(() => {
            ttsFinishTimerRef.current = null;
            setAvatarSpeaking(false);
        }, Math.max(delay, 4000));
    }, []);

    const leaveInquiry = useCallback(() => {
        if (leavingRef.current && phase === "landing") return;
        leavingRef.current = true;
        clearTtsFinishTimer();
        if (sessionId) {
            sendMessage({
                type: "end_session",
                sessionId,
                timestamp: Date.now(),
            });
        }
        sessionNonceRef.current += 1;
        setSessionControl({ action: "end", nonce: sessionNonceRef.current });
        digitalHumanRef.current?.exit();
        sessionStartedRef.current = false;
        avatarRetryRef.current = 0;
        avatarReadyRef.current = false;
        browserAudioFallbackRef.current = false;
        pendingTtsRef.current = [];
        pendingTtsFinishRef.current = false;
        browserChunksRef.current = [];
        ttsTurnBytesRef.current = 0;
        ttsTurnStartRef.current = 0;
        stopBrowserTts();
        setAvatarReady(false);
        setAvatarSpeaking(false);
        setAvatarError(null);
        setBrowserAudioFallback(false);
        setIsSessionActive(false);
        setSessionId(null);
        setMessages([]);
        setPhase("landing");
        window.setTimeout(() => {
            leavingRef.current = false;
        }, 0);
    }, [phase, sessionId, sendMessage, stopBrowserTts]);

    const enterInquiry = useCallback(() => {
        if (!isConnected || phase === "inquiry") return;
        leavingRef.current = false;
        sessionStartedRef.current = false;
        avatarRetryRef.current = 0;
        setAvatarError(null);
        setAvatarReady(false);
        setAvatarSpeaking(false);
        setBrowserAudioFallback(false);
        avatarReadyRef.current = false;
        browserAudioFallbackRef.current = false;
        pendingTtsRef.current = [];
        pendingTtsFinishRef.current = false;
        browserChunksRef.current = [];
        stopBrowserTts();
        // 用户手势里预热扬声器，避免并发降级后欢迎语被浏览器拦截
        const warm = new Audio();
        warm.src =
            "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=";
        void warm.play().catch(() => undefined);
        setPhase("inquiry");
    }, [isConnected, phase, stopBrowserTts]);

    const handleManualReconnect = useCallback(() => {
        reconnect();
        if (phase === "inquiry") {
            setAvatarError(null);
            setAvatarReady(false);
            setAvatarSpeaking(false);
            setBrowserAudioFallback(false);
            avatarReadyRef.current = false;
            browserAudioFallbackRef.current = false;
            pendingTtsRef.current = [];
            pendingTtsFinishRef.current = false;
            browserChunksRef.current = [];
            stopBrowserTts();
            sessionStartedRef.current = false;
            digitalHumanRef.current?.exit();
            window.setTimeout(() => {
                digitalHumanRef.current?.start();
            }, 200);
        }
    }, [phase, reconnect, stopBrowserTts]);

    const isAvatarPlayable = useCallback(() => {
        if (browserAudioFallbackRef.current) return false;
        const avatar = digitalHumanRef.current;
        return (
            avatarReadyRef.current &&
            !!avatar &&
            (typeof avatar.isReady !== "function" || avatar.isReady())
        );
    }, []);

    const playBrowserCollected = useCallback(() => {
        const chunks = browserChunksRef.current;
        browserChunksRef.current = [];
        if (chunks.length === 0) {
            setAvatarSpeaking(false);
            return;
        }
        const merged = concatArrayBuffers(chunks);
        void playBrowserTts(new Uint8Array(merged));
    }, [playBrowserTts]);

    const flushPendingTts = useCallback(() => {
        if (browserAudioFallbackRef.current) {
            browserChunksRef.current = [
                ...browserChunksRef.current,
                ...pendingTtsRef.current,
            ];
            pendingTtsRef.current = [];
            if (pendingTtsFinishRef.current) {
                pendingTtsFinishRef.current = false;
                setAvatarSpeaking(true);
                playBrowserCollected();
            }
            return;
        }
        if (!isAvatarPlayable()) return;
        const avatar = digitalHumanRef.current;
        if (!avatar) return;
        const queued = pendingTtsRef.current;
        const shouldFinish = pendingTtsFinishRef.current;
        if (queued.length === 0 && !shouldFinish) return;
        pendingTtsRef.current = [];
        pendingTtsFinishRef.current = false;
        setAvatarSpeaking(true);
        ttsTurnStartRef.current = Date.now();
        for (const buffer of queued) {
            lastChunkBytesRef.current = buffer.byteLength;
            avatar.playTTSAudio(buffer);
        }
        if (shouldFinish) {
            avatar.finishTTSAudio();
            scheduleAvatarSpeakingRelease();
        }
    }, [isAvatarPlayable, playBrowserCollected, scheduleAvatarSpeakingRelease]);

    const handleTTSStart = useCallback((sampleRate?: number) => {
        if (typeof sampleRate === "number" && sampleRate > 0) {
            ttsSampleRateRef.current = sampleRate;
        } else {
            ttsSampleRateRef.current = TTS_TAIL_CONFIG.SAMPLE_RATE;
        }
        clearTtsFinishTimer();
        pendingTtsFinishRef.current = false;
        pendingTtsRef.current = [];
        ttsTurnBytesRef.current = 0;
        ttsTurnStartRef.current = 0;
        lastChunkAtRef.current = 0;
        lastChunkBytesRef.current = 0;
        setAvatarSpeaking(true);
        if (browserAudioFallbackRef.current) {
            stopBrowserTts();
            browserChunksRef.current = [];
            return;
        }
        digitalHumanRef.current?.beginTTSTurn();
    }, [stopBrowserTts]);

    const handleAudioChunk = useCallback(
        (audioBase64: string | null, isLast: boolean) => {
            const useBrowser = browserAudioFallbackRef.current;
            const playable = !useBrowser && isAvatarPlayable();

            if (audioBase64) {
                try {
                    const buffer = toEvenArrayBuffer(
                        decodeBase64ToArrayBuffer(audioBase64),
                    );
                    lastChunkBytesRef.current = buffer.byteLength;
                    lastChunkAtRef.current = Date.now();
                    if (ttsTurnBytesRef.current === 0) {
                        ttsTurnStartRef.current = Date.now();
                    }
                    ttsTurnBytesRef.current += buffer.byteLength;
                    clearTtsFinishTimer();
                    pendingTtsFinishRef.current = false;
                    if (useBrowser) {
                        browserChunksRef.current.push(buffer);
                    } else if (playable) {
                        digitalHumanRef.current?.playTTSAudio(buffer);
                    } else {
                        const queuedBytes = pendingTtsRef.current.reduce(
                            (sum, item) => sum + item.byteLength,
                            0,
                        );
                        if (
                            queuedBytes + buffer.byteLength <=
                            TTS_TAIL_CONFIG.PENDING_MAX_BYTES
                        ) {
                            pendingTtsRef.current.push(buffer);
                        } else {
                            console.warn(
                                "[TTS] 数字人未就绪，待播队列已满，丢弃后续分片",
                            );
                        }
                    }
                } catch (e) {
                    console.error("解码音频失败", e);
                }
            }

            if (isLast) {
                if (useBrowser) {
                    pendingTtsFinishRef.current = false;
                    clearTtsFinishTimer();
                    playBrowserCollected();
                } else if (playable) {
                    pendingTtsFinishRef.current = false;
                    digitalHumanRef.current?.finishTTSAudio();
                    scheduleAvatarSpeakingRelease();
                } else {
                    pendingTtsFinishRef.current = true;
                    // 数字人未就绪时仍要释放倾听锁，否则语音通道会永久卡在「正在播报」
                    scheduleAvatarSpeakingRelease();
                }
            }
        },
        [
            isAvatarPlayable,
            playBrowserCollected,
            scheduleAvatarSpeakingRelease,
        ],
    );

    const handleDigitalHumanReady = useCallback(() => {
        setAvatarError(null);
        avatarRetryRef.current = 0;
        avatarReadyRef.current = true;
        browserAudioFallbackRef.current = false;
        setBrowserAudioFallback(false);
        setAvatarReady(true);
        flushPendingTts();
    }, [flushPendingTts]);

    const handleAvatarPlaybackEnd = useCallback(() => {
        clearTtsFinishTimer();
        setAvatarSpeaking(false);
    }, []);

    const handleDigitalHumanError = useCallback((error: {
        message?: string;
        terminate?: boolean;
        code?: string;
        busy?: boolean;
    }) => {
        const msg = formatAvatarSessionError(error?.message, error?.code);
        const busy =
            error?.busy || isAvatarBusyError(error?.message, error?.code);
        setAvatarError(msg);
        avatarReadyRef.current = false;
        setAvatarReady(false);
        if (busy) {
            browserAudioFallbackRef.current = true;
            setBrowserAudioFallback(true);
            if (pendingTtsRef.current.length > 0) {
                browserChunksRef.current.push(...pendingTtsRef.current);
                pendingTtsRef.current = [];
            }
            if (pendingTtsFinishRef.current) {
                pendingTtsFinishRef.current = false;
                playBrowserCollected();
            }
            return;
        }
        setAvatarSpeaking(false);
        if (phase !== "inquiry" || leavingRef.current) return;
        if (avatarRetryRef.current < 1) {
            avatarRetryRef.current += 1;
            window.setTimeout(() => {
                if (leavingRef.current) return;
                digitalHumanRef.current?.start();
            }, 2500);
        }
    }, [phase, playBrowserCollected]);

    const handleSessionChange = useCallback(
        (id: string | null) => {
            setSessionId(id);
            setIsSessionActive(!!id);
            if (
                !id &&
                isConnected &&
                phase === "inquiry" &&
                !leavingRef.current
            ) {
                leaveInquiry();
            }
        },
        [phase, isConnected, leaveInquiry],
    );

    const handleMessageUpdate = useCallback((newMessages: ChatMessage[]) => {
        setMessages(newMessages);
    }, []);

    useEffect(() => {
        if (phase === "inquiry" && !isConnected) {
            sessionStartedRef.current = false;
        }
    }, [phase, isConnected]);

    useEffect(() => {
        if (
            phase === "inquiry" &&
            isConnected &&
            !sessionStartedRef.current
        ) {
            sessionStartedRef.current = true;
            sessionNonceRef.current += 1;
            setSessionControl({
                action: "start",
                nonce: sessionNonceRef.current,
            });
        }
    }, [phase, isConnected]);

    useEffect(() => {
        return () => clearTtsFinishTimer();
    }, []);

    return (
        <div className="app">
            <StatusBar
                isConnected={isConnected}
                isSessionActive={isSessionActive}
                sessionId={sessionId}
                messageCount={messages.length}
                reconnectAttempts={reconnectAttempts}
                maxReconnectAttempts={maxReconnectAttempts}
                reconnectExhausted={reconnectExhausted}
                phase={phase}
                avatarReady={avatarReady}
                avatarError={avatarError}
                onReconnect={handleManualReconnect}
                onEndInquiry={phase === "inquiry" ? leaveInquiry : undefined}
            />

            {phase === "landing" ? (
                <div className="landing">
                    <button
                        type="button"
                        className={`landing-cta ${!isConnected ? "disabled" : ""}`}
                        onClick={enterInquiry}
                        disabled={!isConnected}
                    >
                        <span className="landing-cta-rings" aria-hidden>
                            <span />
                            <span />
                            <span />
                        </span>
                        <span className="landing-cta-glow" />
                        <span className="landing-cta-label">
                            开启数字人AI问询
                        </span>
                    </button>
                    <p className="landing-hint">
                        {isConnected
                            ? "连接已就绪，点击进入问询"
                            : reconnectExhausted
                              ? "通信中断，请点击右上角「一键重连」"
                              : "正在建立通信连接…"}
                    </p>
                </div>
            ) : (
                <div className="main-layout">
                    <div className="digital-human-section">
                        <DigitalHumanChat
                            ref={digitalHumanRef}
                            baseUrl={AVATAR_HTTP_CONFIG.URL}
                            autoStart
                            showLauncher={false}
                            onReady={handleDigitalHumanReady}
                            onPlaybackEnd={handleAvatarPlaybackEnd}
                            onError={handleDigitalHumanError}
                            onMessage={(msg) =>
                                console.log("数字人消息:", msg.text)
                            }
                        />
                    </div>
                    <div className="chat-section">
                        <ChatPanel
                            isConnected={isConnected}
                            sendMessage={sendMessage}
                            subscribe={subscribe}
                            sessionControl={sessionControl}
                            onSessionChange={handleSessionChange}
                            onSessionStartingChange={setIsSessionStarting}
                            onMessageUpdate={handleMessageUpdate}
                            onAudioChunk={handleAudioChunk}
                            onTTSStart={handleTTSStart}
                            avatarReady={avatarReady}
                            avatarSpeaking={avatarSpeaking}
                            avatarError={avatarError}
                            browserAudioFallback={browserAudioFallback}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default App;
